/**
 * Search Filters - Metafield-based filtering for movie posters
 * All filtering uses product metafields, not tags
 * Requires jQuery
 */

function uniqueSort(arr) {
    const unique = [...new Set(arr)];

    return unique.sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }

        return String(a).localeCompare(String(b));
    });
}

(function ($) {
  'use strict'

  // ============================================================
  // Filter State Manager
  // ============================================================
  var FilterState = {
    genre: [],
    actors: [],
    decade: [],
    franchise: [],
    size: [],
    price: [],
    query: ''
  }

  // Expose for debugging
  window.DebugFilterState = FilterState

  // ============================================================
  // Search Engine - Tokenized fuzzy matching
  // ============================================================
  var SearchEngine = {
    matches: function (product, query) {
      if (!query) return true;

      const tokens = query.toLowerCase().trim().split(/\s+/).filter(t => t.length > 0);
      if (tokens.length === 0) return true;

      const searchSpace = [
        product.title || '',
        ...ProductHelpers.getGenres(product),
        ...ProductHelpers.getActors(product),
        ...ProductHelpers.getFranchises(product)
      ].join(' ').toLowerCase();

      // All tokens must match (AND logic)
      return tokens.every(token => searchSpace.includes(token));
    }
  }

  // ============================================================
  // Facet Engine - Dynamic, count-aware filter generation
  // ============================================================
  var FacetEngine = {
    getCounts: function (products, type) {
      const counts = {};
      const helper = this.getHelperForType(type);

      products.forEach(p => {
        const values = helper.call(ProductHelpers, p);
        values.forEach(v => {
          counts[v] = (counts[v] || 0) + 1;
        });
      });

      return counts;
    },

    getHelperForType: function (type) {
      const map = {
        genre: ProductHelpers.getGenres,
        actors: ProductHelpers.getActors,
        franchise: ProductHelpers.getFranchises,
        decade: ProductHelpers.getDecade, // returns string, not array
        size: function(p) { return ProductHelpers.getSize(p) ? [ProductHelpers.getSize(p)] : [] }
      };
      return map[type] || function() { return []; };
    },

    // Specialized handler for Decade since it returns a single string
    getDecadeCounts: function (products) {
      const counts = {};
      products.forEach(p => {
        const d = ProductHelpers.getDecade(p);
        if (d) counts[d] = (counts[d] || 0) + 1;
      });
      return counts;
    },

    generateFacetData: function (products, type) {
      let counts;
      if (type === 'decade') {
        counts = this.getDecadeCounts(products);
      } else {
        counts = this.getCounts(products, type);
      }

      // Return sorted unique values with counts
      return Object.keys(counts).map(val => ({
        value: val,
        count: counts[val]
      })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    }
  }

  // ============================================================
  var ProductHelpers = {
    // Get metafield value safely — supports both nested (Liquid) and flat (JSON) formats
    getMetafield: function (product, namespace, key) {
      if (!product.metafields) return null
      // Flat format from products.json: metafields.Genre, metafields.Actor, etc.
      if (product._flatMetafields) {
        return product.metafields[key] || null
      }
      // Nested format from Liquid: metafields.Products.Genre
      var ns = product.metafields[namespace]
      if (!ns) return null
      var value = ns[key]
      return value || null
    },

    // Parse comma-separated list from metafield
    parseMetafieldList: function (value) {
      if (!value) return []
      var str = String(value)
      return str.split(',').map(function (s) {
        return s.trim().replace(/["'[\]]/g, '')
      }).filter(function (s) {
        return s.length > 0
      })
    },

    // Get product genres from metafields.Products.Genre
    getGenres: function (product) {
      var value = this.getMetafield(product, 'Products', 'Genre')
      return this.parseMetafieldList(value)
    },

    // Get product actors from metafields.Products.Actor
    getActors: function (product) {
      var value = this.getMetafield(product, 'Products', 'Actor')
      return this.parseMetafieldList(value)
    },

    // Get product sizes from metafields.products.size
    getSize: function (product) {
      var value = this.getMetafield(product, 'products', 'size')
      if (value) {
        var sizes = this.parseMetafieldList(value)
        return sizes.length > 0 ? sizes[0] : ''
      }
      return ''
    },

    // Get product year from metafields.Products.Year
    getYear: function (product) {
      var value = this.getMetafield(product, 'Products', 'Year')
      if (value) {
        var years = this.parseMetafieldList(value)
        if (years.length > 0) {
          var match = years[0].match(/\d{4}/)
          if (match) return match[0]
        }
      }
      return ''
    },

    // Get decade from year
    getDecade: function (product) {
      var year = this.getYear(product)
      if (!year) return ''
      var y = parseInt(year, 10)
      if (!y || y < 1900 || y > 2099) return ''
      return Math.floor(y / 10) * 10 + 's'
    },

    // Get product franchises from metafields.Products.Franchise
    getFranchises: function (product) {
      var value = this.getMetafield(product, 'Products', 'Franchise')
      return this.parseMetafieldList(value)
    }
  }

  // ============================================================
  // Product Card Renderer
  // ============================================================
  var ProductRenderer = {
    escapeHtml: function (str) {
      return $('<div>').text(str).html()
    },

    getImageUrl: function (product) {
      if (product.featured_image) {
        return typeof product.featured_image === 'string'
          ? product.featured_image
          : (product.featured_image.src || '')
      }
      if (product.images && product.images.length > 0) {
        return typeof product.images[0] === 'string'
          ? product.images[0]
          : (product.images[0].src || '')
      }
      return ''
    },

    formatPrice: function (product) {
      var cents = product.price || product.price_min || 0
      if (!cents) return ''
      return '$' + (cents / 100).toFixed(0)
    },

    getProductUrl: function (product) {
      return product.url || '/products/' + (product.handle || '')
    },

    renderCard: function (product) {
      var imageUrl = this.getImageUrl(product)
      var title = this.escapeHtml(product.title || '')
      var size = this.escapeHtml(ProductHelpers.getSize(product))
      var price = this.formatPrice(product)
      var productUrl = this.escapeHtml(this.getProductUrl(product))
      var productId = product.id || product.handle
      var decade = ProductHelpers.getDecade(product)

      var imgHtml = imageUrl
        ? '<div class="img-zoom-container"><img src="' + imageUrl + '" class="img-fluid white-inlay w-100" alt="' + title + '" loading="lazy"></div>'
        : '<div class="placeholder-img"><span>No Image</span></div>'

      var sizePriceHtml = ''
      if (size) sizePriceHtml += '<span>' + size + '</span><br>'
      if (price) sizePriceHtml += '<span class="featured-products__price">' + price + '</span>'

      return '<div class="col product-card" data-product-id="' + productId + '" data-decade="' + this.escapeHtml(decade) + '">' +
        '<a href="' + productUrl + '" class="link-reset text-decoration-none text-reset d-inline-block" tabindex="-1" title="' + title + '">' +
          '<div class="featured-products__product position-relative overflow-hidden flex-shrink-0 p-4">' +
            imgHtml +
          '</div>' +
          '<div class="featured-products__info py-2 text-start">' +
            '<div class="featured-products__info-line1 global-context fw-bold lh-sm mb-1" style="font-size: 14px; color: #3c3c3c;">' +
              '<strong>' + title + '</strong>' +
            '</div>' +
            '<div class="featured-products__info-line2 global-context">' +
              sizePriceHtml +
            '</div>' +
            '<div class="details-link">Details <span class="details-link-chevron"></span></div>' +
          '</div>' +
        '</a>' +
      '</div>'
    },

    renderNoResults: function () {
      return '<div class="col-18 py-5 text-center text-muted">' +
        '<p class="mb-0">No results found.</p>' +
      '</div>'
    },

    renderAll: function (products) {
      var $grid = $('#search-results-grid')

      // Check if products are already rendered server-side
      var $existingCards = $grid.find('.product-card')

      if ($existingCards.length > 0) {
        // Products are server-side rendered, just initialize filters
        return
      }

      // Client-side rendering (fallback)
      $grid.empty()

      if (products && products.length > 0) {
        var html = ''
        $.each(products, function (i, product) {
          html += ProductRenderer.renderCard(product)
        })
        $grid.html(html)
      } else {
        $grid.html(this.renderNoResults())
      }
    }
  }

  // ============================================================
  // Filter Engine - Core matching and UI synchronization
  // ============================================================
  var FilterEngine = {
    _debugLogged: false,

    // Check if product matches current filters + search query
    matches: function (product) {
      // Search query check (MUST match all tokens)
      if (!SearchEngine.matches(product, FilterState.query)) return false;

      var genres = ProductHelpers.getGenres(product)
      var actors = ProductHelpers.getActors(product)
      var franchises = ProductHelpers.getFranchises(product)
      var size = ProductHelpers.getSize(product)
      var decade = ProductHelpers.getDecade(product)

      // Genre filter - match ANY selected genre
      if (FilterState.genre.length > 0) {
        if (!FilterState.genre.some(g => $.inArray(g, genres) !== -1)) return false;
      }

      // Actor filter - match ANY selected actor
      if (FilterState.actors.length > 0) {
        if (!FilterState.actors.some(a => $.inArray(a, actors) !== -1)) return false;
      }

      // Size filter - match ANY selected size
      if (FilterState.size.length > 0) {
        if (!FilterState.size.some(s => size && size.toLowerCase() === s.toLowerCase())) return false;
      }

      // Decade filter - match ANY selected decade
      if (FilterState.decade.length > 0) {
        if (!decade || !FilterState.decade.some(d => d === decade)) return false;
      }

      // Franchise filter - match ANY selected franchise
      if (FilterState.franchise.length > 0) {
        if (!FilterState.franchise.some(f => $.inArray(f, franchises) !== -1)) return false;
      }

      // Price filter - match ANY selected band
      if (FilterState.price.length > 0) {
        var cents = product.price || product.price_min || 0
        var dollars = cents / 100
        if (!FilterState.price.some(token => {
          var parts = token.split('-');
          return dollars >= parseFloat(parts[0]) && dollars < parseFloat(parts[1]);
        })) return false;
      }

      return true;
    },

    // Return sorted unique values for a filter type (alphabetical, natural order)
    getSortedUniqueValues: function (products, type) {
      var values = [];
      products.forEach(function (p) {
        var vals = null;
        switch (type) {
          case 'genre':
            vals = ProductHelpers.getGenres(p);
            break;
          case 'actors':
            vals = ProductHelpers.getActors(p);
            break;
          case 'decade':
            vals = [ProductHelpers.getDecade(p)].filter(function (d) { return d; });
            break;
          case 'franchise':
            vals = ProductHelpers.getFranchises(p);
            break;
          case 'size':
            var size = ProductHelpers.getSize(p);
            vals = size ? [size] : [];
            break;
        }
        if (vals && vals.length) {
          vals.forEach(function (v) {
            if (v && values.indexOf(v) === -1) {
              values.push(v);
            }
          });
        }
      });
      // Natural alphabetical sort (case-insensitive)
      values.sort(function (a, b) {
        var aLower = String(a).toLowerCase();
        var bLower = String(b).toLowerCase();
        return aLower.localeCompare(bLower);
      });
      return values;
    },

    // Source catalog: products loaded + scoped + normalized by BrowseAllLoader
    // and stored on window.searchResults during initWithProducts().
    sourceProducts: function () {
      return window.searchResults || []
    },

    // Return products matching current filters (without updating UI)
    getFilteredProducts: function () {
      var self = this
      return this.sourceProducts().filter(function (p) {
        return self.matches(p)
      })
    },

    // Main application loop: Filter -> Render
    apply: function () {
      var scoped = this.sourceProducts()
      var filtered = this.getFilteredProducts()

      // 1. Grid & Pagination
      ClientPagination.reset();
      ClientPagination.render(filtered);

      // 2. Result Counts & UI
      this.updateResultCount(filtered.length, scoped.length);

      // 3. Dynamic Facets (Counts)
      this.refreshFacets(filtered);

      Badges.refresh();
    },

    refreshFacets: function (filtered) {
      const types = ['genre', 'actors', 'decade', 'franchise', 'size'];

      types.forEach(function (type) {
        var $containers = $('.' + type + '-filters');
        if (!$containers.length) return;

        // Get sorted unique values for this filter type
        var values = FilterEngine.getSortedUniqueValues(filtered, type);

        // Render into every matching panel (desktop sidebar + mobile modal),
        // namespacing input ids by each container's data-prefix to avoid
        // id collisions between the duplicated desktop/mobile markup.
        $containers.each(function () {
          var $container = $(this);
          var prefix = $container.data('prefix') || '';

          // Clear existing checkboxes (idempotent re-render)
          $container.find('.filter-checkbox').remove();

          var $link = $container.find('.filter-pagination-link').closest('.text-center');

          values.forEach(function (val) {
            var inputId = prefix + type + '-' + val.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            var isChecked = $.inArray(val, FilterState[type]) !== -1 ? ' checked' : '';

            var html = '<div class="filter-checkbox">' +
              '<input type="checkbox" class="form-check-input" id="' + inputId + '"' +
                ' name="' + type + '" value="' + val + '"' +
                ' data-filter-type="' + type + '" data-filter-value="' + val + '"' + isChecked + '>' +
              '&nbsp;<label class="form-check-label" for="' + inputId + '">' + val + '</label>' +
              '</div>';

            if ($link.length) {
              $(html).insertBefore($link);
            } else {
              $container.append(html);
            }
          });

          // Hide empty-message if we have values, show it if empty
          var $emptyMsg = $container.find('.text-muted.small');
          if (values.length > 0) {
            $emptyMsg.hide();
          } else {
            $emptyMsg.show();
          }

          // Apply pagination visibility
          FilterPagination.updateVisibility($container);
        });
      });
    },

    updateResultCount: function (visibleCount, totalCount) {
      window.searchResultsCount = visibleCount;
      var hasActive = this.hasActiveFilters();
      $('.js-clear-filters').toggle(hasActive);
    },

    hasActiveFilters: function () {
      return FilterState.genre.length > 0 ||
             FilterState.actors.length > 0 ||
             FilterState.franchise.length > 0 ||
             FilterState.size.length > 0 ||
             FilterState.decade.length > 0 ||
             FilterState.price.length > 0 ||
             FilterState.query !== '';
    },

    clearAll: async function () {
      FilterState.genre = [];
      FilterState.actors = [];
      FilterState.franchise = [];
      FilterState.size = [];
      FilterState.decade = [];
      FilterState.price = [];
      FilterState.query = '';

      $('[data-filter-type]:checked').prop('checked', false);
      $('#search-input, #search-input-mobile').val('');

      FilterPagination.resetAll();
      await this.apply();
    }
  }

  // ============================================================
  // Accordion header badges — show active selection count per filter type.
  // Drives [data-badge-for="{type}"] spans in the mobile filter modal.
  // ============================================================
  var Badges = {
    TYPES: ['genre', 'actors', 'decade', 'franchise', 'size', 'price'],

    refresh: function () {
      this.TYPES.forEach(function (type) {
        var count = (FilterState[type] || []).length
        $('[data-badge-for="' + type + '"]').text(count > 0 ? '(' + count + ')' : '')
      })
    }
  }

  // ============================================================
  // Price Range — $500-interval band checkboxes
  // ============================================================
  var PriceRange = {
    BAND_SIZE: 500,
    // Above this, prices are sparse (rare high-value posters). One open-ended
    // band collects them instead of hundreds of mostly-empty $500 bands.
    CAP: 5000,

    // Build the list of price bands {lo, hi, label} from $0 up to max.
    // Fixed $500 bands up to CAP, then a single "$CAP+" band for the long tail.
    buildBands: function (max) {
      var bands = []
      var cap = Math.min(this.CAP, max)
      for (var lo = 0; lo < cap; lo += this.BAND_SIZE) {
        var hi = lo + this.BAND_SIZE
        bands.push({ lo: lo, hi: hi, label: '$' + lo + ' - $' + hi })
      }
      if (max > this.CAP) {
        // Open-ended top band; hi set just past max so the lower-inclusive
        // [lo, hi) check in FilterEngine.matches still captures the priciest item.
        var top = Math.ceil(max) + 1
        bands.push({ lo: this.CAP, hi: top, label: '$' + this.CAP + '+' })
      }
      return bands
    },

    // Calculate min and max prices from products
    getPriceRange: function () {
      var products = window.searchResults || []
      if (products.length === 0) {
        return { min: 10, max: 1000 }
      }
      var minPrice = Infinity
      var maxPrice = 0
      products.forEach(function (product) {
        var price = product.price || product.price_min || 0
        var dollars = price / 100
        if (dollars < minPrice) minPrice = dollars
        if (dollars > maxPrice) maxPrice = dollars
      })
      return {
        min: Math.floor(minPrice),
        max: Math.ceil(maxPrice)
      }
    },

    // Build $500 band checkboxes from $0 to ceil(max price).
    // Renders into every .price-filters panel (desktop sidebar + mobile modal),
    // namespacing input ids by each container's data-prefix to avoid id collisions.
    renderBands: function () {
      var $containers = $('.price-filters')
      if (!$containers.length) return

      var max = Math.ceil(this.getPriceRange().max)
      var selected = FilterState.price
      var bands = this.buildBands(max)

      $containers.each(function () {
        var $container = $(this)
        var prefix = $container.data('prefix') || ''
        var $empty = $container.find('.price-filters-empty')
        var $link = $container.find('.filter-pagination-link').closest('.text-center')

        // Remove any previously rendered bands (idempotent re-render)
        $container.find('.filter-checkbox').remove()

        if (!max || max <= 0 || bands.length === 0) {
          $empty.show()
          return
        }

        var html = ''
        bands.forEach(function (band) {
          var token = band.lo + '-' + band.hi
          var inputId = prefix + 'price-' + token
          var isChecked = $.inArray(token, selected) !== -1 ? ' checked' : ''
          html += '<div class="filter-checkbox">' +
            '<input type="checkbox" class="form-check-input" id="' + inputId + '"' +
            ' name="price" value="' + token + '"' +
            ' data-filter-type="price" data-filter-value="' + token + '"' + isChecked + '>' +
            '&nbsp;<label class="form-check-label" for="' + inputId + '">' + band.label + '</label>' +
            '</div>'
        })

        // Insert bands before the show-more link wrapper
        if ($link.length) {
          $(html).insertBefore($link)
        } else {
          $container.append(html)
        }
        $empty.hide()

        // Apply pagination visibility
        FilterPagination.updateVisibility($container)
      })
    }
  }

  // ============================================================
  // Filter Pagination - Show 10 items at a time
  // ============================================================
  var ClientPagination = {
    currentPage: 1,
    itemsPerPage: 100,

    reset: function () {
      this.currentPage = 1
    },

    totalPages: function (totalItems) {
      return Math.ceil(totalItems / this.itemsPerPage)
    },

    pageSlice: function (products) {
      var start = (this.currentPage - 1) * this.itemsPerPage
      return products.slice(start, start + this.itemsPerPage)
    },

    render: function (filteredProducts) {
      var total = filteredProducts.length
      var pages = this.totalPages(total)
      var current = this.currentPage
      var $container = $('#browse-all-pagination')
      var $grid = $('#search-results-grid')
      var $noResults = $('#browse-all-no-results')

      // Render current page cards
      $grid.empty()
      var slice = this.pageSlice(filteredProducts)
      if (slice.length > 0) {
        var html = ''
        $.each(slice, function (i, product) {
          html += ProductRenderer.renderCard(product)
        })
        $grid.html(html).show()
        $noResults.hide()
      } else {
        $grid.hide()
        $noResults.show()
      }

      // Render pagination controls
      if (pages <= 1) {
        $container.hide().html('')
        return
      }

      var start = (current - 1) * this.itemsPerPage + 1
      var end = Math.min(current * this.itemsPerPage, total)
      var maxVisible = 5
      var startPage = Math.max(1, current - 2)
      var endPage = Math.min(pages, startPage + maxVisible - 1)
      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1)
      }

      var html = '<div class="pagination-wrapper">'
      html += '<div class="pagination-info">Showing ' + start + '-' + end + ' of ' + total + ' products</div>'
      html += '<nav aria-label="Pagination"><ul class="pagination-list">'

      if (current > 1) {
        html += '<li class="pagination-item"><a class="pagination-link pagination-link--first" href="#" data-page="1">First</a></li>'
        html += '<li class="pagination-item"><a class="pagination-link pagination-link--prev" href="#" data-page="' + (current - 1) + '">&lt;</a></li>'
      }

      for (var i = startPage; i <= endPage; i++) {
        if (i === current) {
          html += '<li class="pagination-item"><span class="pagination-link active" aria-current="page">' + i + '</span></li>'
        } else {
          html += '<li class="pagination-item"><a class="pagination-link" href="#" data-page="' + i + '">' + i + '</a></li>'
        }
      }

      if (current < pages) {
        html += '<li class="pagination-item"><a class="pagination-link pagination-link--next" href="#" data-page="' + (current + 1) + '">&gt;</a></li>'
        html += '<li class="pagination-item"><a class="pagination-link pagination-link--last" href="#" data-page="' + pages + '">Last</a></li>'
      }

      html += '</ul></nav></div>'
      $container.html(html).show()
    },

    init: function () {
      var self = this
      $(document).on('click', '#browse-all-pagination [data-page]', function (e) {
        e.preventDefault()
        var page = parseInt($(this).data('page'), 10)
        if (!page) return
        self.currentPage = page
        var filtered = FilterEngine.getFilteredProducts()
        self.render(filtered)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }
  }

  // ============================================================
  // Filter Pagination — "Show more / less" within each filter group.
  // Per-container state so the desktop sidebar and mobile modal paginate
  // independently.
  // ============================================================
  var FilterPagination = {
    itemsPerPage: 10,
    CONTAINER_SELECTOR: '.genre-filters, .actors-filters, .decade-filters, .franchise-filters, .size-filters, .price-filters',

    // Read (and lazily init) the {page, expanded} state stored on a container.
    getState: function ($container) {
      var state = $container.data('fp')
      if (!state) {
        state = { page: 1, expanded: false }
        $container.data('fp', state)
      }
      return state
    },

    // Update visibility for a single container based on its own state
    updateVisibility: function ($container) {
      var $items = $container.find('.filter-checkbox')
      var totalItems = $items.length
      var state = this.getState($container)
      var maxVisible = state.page * this.itemsPerPage

      $items.each(function (index) {
        if (state.expanded || index < maxVisible) {
          $(this).show()
        } else {
          $(this).hide()
        }
      })

      this.updateLink($container, totalItems)
    },

    // Update the show more/less link visibility and text for one container
    updateLink: function ($container, totalItems) {
      var state = this.getState($container)
      var $link = $container.find('.filter-pagination-link')
      var $wrapper = $link.closest('.text-center')

      if (!$link.length) return

      // Price filters never show pagination link
      if ($container.hasClass('price-filters')) {
        $wrapper.hide()
        return
      }

      var maxVisible = state.page * this.itemsPerPage

      if (state.expanded) {
        $link.text('Show less')
        $wrapper.show()
      } else if (totalItems > this.itemsPerPage) {
        $link.text('Show more')
        $wrapper.show()
      } else {
        $wrapper.hide()
      }
    },

    // Show more items in one container
    showMore: function ($container) {
      var state = this.getState($container)
      state.page++
      state.expanded = true
      this.updateVisibility($container)
    },

    // Collapse one container back to first page
    showLess: function ($container) {
      var state = this.getState($container)
      state.page = 1
      state.expanded = false
      this.updateVisibility($container)
    },

    // Reset every container (both panels) to initial state
    resetAll: function () {
      var self = this
      $(this.CONTAINER_SELECTOR).each(function () {
        var $container = $(this)
        var state = self.getState($container)
        state.page = 1
        state.expanded = false
        self.updateVisibility($container)
      })
    },

    // Initialize pagination for all filter containers across both panels
    init: function () {
      var self = this

      // Delegated click handler — resolve state from the clicked container
      $(document).on('click', '.filter-pagination-link', function (e) {
        e.preventDefault()
        var $el = $(this)
        var $container = $el.closest(self.CONTAINER_SELECTOR)
        if (!$container.length) return

        if ($el.text() === 'Show more') {
          self.showMore($container)
        } else {
          self.showLess($container)
        }
      })

      // Initialize visibility for every container
      $(this.CONTAINER_SELECTOR).each(function () {
        self.updateVisibility($(this))
      })
    }
  }

  // ============================================================
  // Filter URL Sync — persist filter state in URL params across pagination
  // Only active on collection/search pages (not browse-all)
  // ============================================================
  var FilterURL = {
    ARRAY_PARAMS: ['f_genre', 'f_actors', 'f_decade', 'f_franchise', 'f_size', 'f_price'],
    STATE_MAP: {
      f_genre: 'genre',
      f_actors: 'actors',
      f_decade: 'decade',
      f_franchise: 'franchise',
      f_size: 'size',
      f_price: 'price'
    },

    // Write current FilterState into URL without reloading.
    // If on page 2+, navigate to page 1 so user sees results from the start.
    push: function () {
      if (window.isBrowseAll) return
      var wasOnPage = new URLSearchParams(window.location.search).get('page')
      var params = new URLSearchParams(window.location.search)

      // Remove all existing filter params
      var self = this
      self.ARRAY_PARAMS.forEach(function (p) { params.delete(p) })
      // Reset to page 1 whenever filters change
      params.delete('page')

      // Write active filters
      FilterState.genre.forEach(function (v) { params.append('f_genre', v) })
      FilterState.actors.forEach(function (v) { params.append('f_actors', v) })
      FilterState.decade.forEach(function (v) { params.append('f_decade', v) })
      FilterState.franchise.forEach(function (v) { params.append('f_franchise', v) })
      FilterState.size.forEach(function (v) { params.append('f_size', v) })
      FilterState.price.forEach(function (v) { params.append('f_price', v) })

      // Sync search query
      if (FilterState.query) {
        params.set('q', FilterState.query);
      } else {
        params.delete('q');
      }

      var qs = params.toString()
      var newUrl = window.location.pathname + (qs ? '?' + qs : '')
      if (wasOnPage && parseInt(wasOnPage, 10) > 1) {
        // Was on page 2+ — hard navigate to page 1 with new filters applied
        window.location.href = newUrl
      } else {
        history.replaceState(null, '', newUrl)
      }
    },

    // Restore FilterState from URL params and check matching boxes
    restore: function () {
      if (window.isBrowseAll) return
      var params = new URLSearchParams(window.location.search)
      var self = this

      // Restore search query
      FilterState.query = params.get('q') || '';

      Object.keys(self.STATE_MAP).forEach(function (param) {
        var stateKey = self.STATE_MAP[param]
        var values = params.getAll(param)
        if (values.length > 0) {
          FilterState[stateKey] = values
          values.forEach(function (val) {
            $('[data-filter-type="' + stateKey + '"][data-filter-value="' + val + '"]').prop('checked', true)
          })
        }
      })
    }
  }

  // ============================================================
  // Filter Checkbox Events
  // ============================================================
  var FilterEvents = {
    debounceTimer: null,

    init: function () {
      // 1. Facet Checkboxes
      $(document).on('change', '[data-filter-type]', function () {
        var $el = $(this)
        var filterType = $el.data('filter-type')
        var value = $el.data('filter-value')

        if ($el.prop('checked')) {
          if ($.inArray(value, FilterState[filterType]) === -1) {
            FilterState[filterType].push(value)
          }
        } else {
          var index = $.inArray(value, FilterState[filterType])
          if (index !== -1) {
            FilterState[filterType].splice(index, 1)
          }
        }

        FilterURL.push()
        FilterEvents.debouncedApply()
      })

      // 2. Search Input (Live - mobile only)
      $('#search-input-mobile').on('input', function () {
        FilterState.query = $(this).val()
        FilterURL.push()
        FilterEvents.debouncedApply()
      })

      // 3. Clear All
      $(document).on('click', '.js-clear-filters', function () {
        FilterEngine.clearAll()
        FilterURL.push()
      })
    },

    debouncedApply: function () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(function () {
        FilterEngine.apply()
      }, 150)
    }
  }

  // ============================================================
  // Browse-All Loader (fetches catalog JSON for the browse-all page)
  // ============================================================
  var BrowseAllLoader = {
    normalizeProduct: function (p) {
      // Map flat productsv2.json shape to what ProductHelpers expects
      return {
        id: p.id,
        title: p.title,
        handle: p.handle,
        url: '/products/' + p.handle,
        price: p.price ? Math.round(parseFloat(p.price) * 100) : 0,
        featured_image: p.image ? { src: p.image } : null,
        images: p.image ? [{ src: p.image }] : [],
        metafields: p.metafields || {},
        _flatMetafields: true
      }
    },

    // Scope raw (pre-normalize) products to the current page context.
    // Collection pages keep only products whose collections include the
    // active handle; search pages keep products matching every search token.
    scope: function (rawProducts) {
      if (window.isCollectionPage && window.collectionHandle) {
        var handle = window.collectionHandle
        return rawProducts.filter(function (p) {
          return p.collections && p.collections.indexOf(handle) !== -1
        })
      }
      if (window.searchTerms) {
        var tokens = String(window.searchTerms).toLowerCase().trim().split(/\s+/).filter(function (t) {
          return t.length > 0
        })
        if (tokens.length === 0) return rawProducts
        return rawProducts.filter(function (p) {
          var space = [p.title, p.handle]
          if (p.metafields) {
            for (var k in p.metafields) {
              if (Object.prototype.hasOwnProperty.call(p.metafields, k)) space.push(p.metafields[k])
            }
          }
          var haystack = space.join(' ').toLowerCase()
          return tokens.every(function (token) {
            return haystack.indexOf(token) !== -1
          })
        })
      }
      return rawProducts
    },

    load: function (onComplete) {
      var url = window.browseAllProductsUrl
      if (!url) {
        onComplete([])
        return
      }

      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status)
          return res.json()
        })
        .then(function (data) {
          var products = Array.isArray(data) ? data : []
          products = BrowseAllLoader.scope(products)
          onComplete(products.map(BrowseAllLoader.normalizeProduct))
        })
        .catch(function (err) {
          console.error('Catalog load failed:', err)
          onComplete([])
        })
    }
  }

  function excludeGiftCards (products) {
    return products.filter(function (p) {
      return !(p.title && p.title.indexOf('Gift Card') !== -1)
    })
  }

  // ============================================================
  function initWithProducts (products) {
    products = excludeGiftCards(products)
    window.searchResults = products
    window.searchResultsCount = products.length
    window.allProductsCount = products.length

    $('#browse-all-loading').hide()

    PriceRange.renderBands()
    FilterEvents.init()
    FilterPagination.init()
    ClientPagination.init()

    // Restore any filter state from the URL before the first render so
    // deep-linked filters apply immediately, then render the filtered grid.
    FilterURL.restore()
    FilterEngine.apply()
  }


  // Reload search results when either q input is cleared via the native
  // type="search" X button (also fires on Enter, which already submits).
  function initSearchInputSync () {
    $('#search-input, #search-input-mobile').on('search', function () {
      if (this.value.trim() === '' && this.form) {
        this.form.submit()
      }
    })
  }

  function init () {
    initSearchInputSync()

    // All page contexts (browse-all, collection, search) load the catalog
    // client-side from productsv2.json. BrowseAllLoader.scope() narrows the
    // raw data to the active collection handle or search terms before render.
    BrowseAllLoader.load(function (products) {
      initWithProducts(products)
    })
  }

  // Run on document ready
  $(document).ready(init)
})(jQuery)
